import { useCallback, useState } from 'react';
import { Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmDocumentsUpload } from 'KnmPackage/KnmDocumentsModule/actions/knmDocumentsActions';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { UploadModal } from 'UIPackage/MainModule/components/UploadModal';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';

import styles from './PmExtension.module.scss';

export const pmExtensionDocumentValidFormats = ['pdf', 'png', 'jpeg', 'ktd', 'rtf', 'rar', 'zip'];
const documentValidExtensionsString = `.${pmExtensionDocumentValidFormats.join(',.')}`;
const maxFileSize = 10 * MB;

interface IPmExtensionFileInputProps {
  formRef: IKnmFormRef | undefined;
  watchOnly: boolean;
  extension: IKnmPmFormExtension;
  parentFieldName: string;
}
export const PmExtensionFileInput = ({
  formRef,
  extension,
  watchOnly,
  parentFieldName,
}: IPmExtensionFileInputProps) => {
  const name = `${parentFieldName}.attachment`;

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { uploadKnmDocumentAttachment, downloadKnmDocumentAttachment } = useKnmDocumentAttachment();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleSaveDocument = useCallback(
    async (signedDocument: File, signatureInfo?: IKnmAttachment['signatureInfo']) => {
      const uploadedAttachment = await uploadKnmDocumentAttachment(signedDocument);

      if (uploadedAttachment) {
        const newUploadedAttachment: IKnmFormAttachment = {
          ...uploadedAttachment,
          signatureInfo: signatureInfo || null,
          signed: !!signatureInfo,
          isNew: true,
        };

        formRef?.current.change(name as keyof IKnmForm, newUploadedAttachment);
      }
    },
    [formRef, uploadKnmDocumentAttachment],
  );

  const handleFileButtonClick = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleModalSave = useCallback(
    (document: File, sign: File | null) => {
      if (sign) {
        dispatch(knmDocumentsUpload(document, [sign], handleSaveDocument));
      } else {
        handleSaveDocument(document);
      }
    },
    [dispatch, handleSaveDocument],
  );

  const handleRemoveFile = useCallback(() => {
    formRef?.current.change(name as keyof IKnmForm, undefined);
  }, []);

  const downloadAttachment = (attachment: IKnmFormAttachment) => () => {
    const { fileName, fileSize } = attachment;
    const filePath = attachment.filePath || null;
    const mimeType = attachment.mimeType || null;

    downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
  };

  const file = extension.attachment;

  return (
    <>
      <Field name={name}>
        {({ meta }) => {
          return (
            <FieldBlock
              label="Файл решения"
              labelFontSize="small"
              color="black"
              id={name}
              button={
                <>
                  {!file && (
                    <Button
                      className="shared-btn-add"
                      text="Добавить"
                      type="button"
                      variant="link"
                      color="primary"
                      disabled={watchOnly}
                      onClick={handleFileButtonClick}
                    />
                  )}
                  {file && (
                    <UploadFile
                      className={styles.FileItem}
                      onRemove={watchOnly ? undefined : handleRemoveFile}
                      onFileNameClick={!file?.isNew && file?.guid ? downloadAttachment(file) : undefined}
                      fileName={file.fileName ?? ''}
                      fileSize={file.fileSize}
                    />
                  )}
                  {(meta.modified || meta.submitFailed) && meta.error && (
                    <p className="shared-input-error">{meta.error}</p>
                  )}
                </>
              }
            />
          );
        }}
      </Field>

      <UploadModal
        accept={documentValidExtensionsString}
        modalTitle="Файл решения"
        onClose={handleModalClose}
        visible={isModalVisible}
        onSave={handleModalSave}
        maxFileSize={maxFileSize}
        uploadButtonTitle="Добавить"
      />
    </>
  );
};
